import React, { useState } from "react";
import {
  Box, Card, Typography, TextField, Button,
  InputAdornment, IconButton, Alert, CircularProgress
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import { apiLogin } from "./adminApi";

export default function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await apiLogin(username, password);
      onLogin();
    } catch (err) {
      setError(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #FFF8E1 0%, #FFF1F1 50%, #FDF3E7 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative circles */}
      {[
        { size: 500, top: -150, left: -150, opacity: 0.05 },
        { size: 300, bottom: -100, right: -100, opacity: 0.05 },
        { size: 200, top: "40%", right: "10%", opacity: 0.04 },
      ].map((c, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: c.size,
            height: c.size,
            borderRadius: "50%",
            border: "1px solid rgba(191,6,3,0.15)",
            top: c.top,
            left: c.left,
            bottom: c.bottom,
            right: c.right,
            opacity: c.opacity * 10,
            pointerEvents: "none",
          }}
        />
      ))}

      <Card
        sx={{
          width: "100%",
          maxWidth: 420,
          mx: 2,
          borderRadius: "24px",
          boxShadow: "0 20px 60px rgba(191,6,3,0.12)",
          background: "#fff",
          border: "1px solid rgba(0,0,0,0.06)",
          p: { xs: 3, sm: 5 },
        }}
      >
        {/* Logo area */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "20px",
              background: "linear-gradient(135deg, #BF0603, #8B0000)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              boxShadow: "0 8px 25px rgba(191,6,3,0.4)",
            }}
          >
            <LockPersonIcon sx={{ color: "#fff", fontSize: 32 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              fontFamily: "Quicksand",
              color: "#222",
              mb: 0.5,
            }}
          >
            Admin Panel
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#777", fontFamily: "Varela Round" }}
          >
            Vivaha Sangamam · Content Manager
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2.5,
              borderRadius: "12px",
              backgroundColor: "rgba(191,6,3,0.08)",
              color: "#8B0000",
              "& .MuiAlert-icon": { color: "#BF0603" },
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            fullWidth
            autoComplete="username"
            sx={inputSx}
          />
          <TextField
            label="Password"
            type={showPwd ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPwd(!showPwd)} edge="end" sx={{ color: "rgba(0,0,0,0.4)" }}>
                    {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.6,
              borderRadius: "14px",
              fontWeight: 800,
              fontFamily: "Quicksand",
              fontSize: "1rem",
              background: "linear-gradient(135deg, #BF0603 0%, #8B0000 100%)",
              boxShadow: "0 6px 20px rgba(191,6,3,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #d40704 0%, #a00000 100%)",
                boxShadow: "0 8px 25px rgba(191,6,3,0.55)",
              },
              "&:disabled": { opacity: 0.6 },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Sign In"}
          </Button>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 3,
            color: "#aaa",
            fontFamily: "Varela Round",
          }}
        >
          Authorised personnel only
        </Typography>
      </Card>
    </Box>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    color: "#222",
    "& fieldset": { borderColor: "rgba(0,0,0,0.18)" },
    "&:hover fieldset": { borderColor: "rgba(191,6,3,0.5)" },
    "&.Mui-focused fieldset": { borderColor: "#BF0603" },
  },
  "& .MuiInputLabel-root": { color: "#888" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#BF0603" },
};
